import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import Html from 'react-pdf-html';

// Standard-Font (Roboto)
Font.register({
    family: 'Roboto',
    fonts: [
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf' },
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf', fontWeight: 'bold' },
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-italic-webfont.ttf', fontStyle: 'italic' }
    ]
});

const STATUS_COLORS = {
    eingereicht: { bg: '#fff7ed', border: '#fdba74', text: '#c2410c', label: 'Eingereicht' },
    genehmigt: { bg: '#f0fdf4', border: '#86efac', text: '#15803d', label: 'Genehmigt' },
    überarbeitung_angefordert: { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569', label: 'Korrektur angefordert' },
    entwurf: { bg: '#f9fafb', border: '#e2e8f0', text: '#94a3b8', label: 'Entwurf' },
    deletion_pending: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', label: 'Löschantrag' }
};

const styles = StyleSheet.create({
    page: {
        paddingTop: 45,
        paddingBottom: 65,
        paddingHorizontal: 40,
        fontFamily: 'Roboto',
        fontSize: 10,
        backgroundColor: '#ffffff'
    },
    coverPage: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 60,
        backgroundColor: '#f8fafc'
    },
    coverTitle: {
        fontSize: 32,
        fontWeight: 'bold',
        marginBottom: 8,
        color: '#0f172a',
        textAlign: 'center'
    },
    coverSubtitle: {
        fontSize: 18,
        marginBottom: 30,
        color: '#475569',
        textAlign: 'center'
    },
    coverInfoBox: {
        backgroundColor: '#ffffff',
        padding: 24,
        borderRadius: 8,
        width: '100%',
        borderLeft: '4pt solid #3b82f6',
        marginHorizontal: 20
    },
    coverLabel: {
        fontSize: 10,
        color: '#64748b',
        marginBottom: 2
    },
    coverValue: {
        fontSize: 12,
        color: '#1e293b',
        marginBottom: 12,
        fontWeight: 'bold'
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderBottomWidth: 2,
        borderBottomColor: '#3b82f6',
        paddingBottom: 10,
        marginBottom: 20
    },
    headerText: {
        fontSize: 9,
        fontWeight: 'bold',
        color: '#3b82f6',
        textTransform: 'uppercase'
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 40,
        right: 40,
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderTopWidth: 1,
        borderTopColor: '#cbd5e1',
        paddingTop: 10
    },
    footerText: {
        fontSize: 8,
        color: '#94a3b8'
    },
    entryContainer: {
        marginBottom: 20,
        borderRadius: 6,
        breakInside: 'avoid',
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: '#e2e8f0'
    },
    entryHeaderRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 10,
        backgroundColor: '#f8fafc',
        borderBottomWidth: 1,
        borderBottomColor: '#e2e8f0'
    },
    entryDateTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#0f172a'
    },
    dayTotalText: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#3b82f6'
    },
    badge: {
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
        fontSize: 8,
        fontWeight: 'bold',
        borderWidth: 1,
        marginLeft: 10
    },
    taskRow: {
        flexDirection: 'row',
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#f1f5f9'
    },
    taskDescription: {
        flex: 1,
        fontSize: 10,
        color: '#334155'
    },
    taskHours: {
        width: 60,
        textAlign: 'right',
        fontSize: 10,
        fontWeight: 'bold',
        color: '#0f172a'
    },
    notesBox: {
        padding: 10,
        backgroundColor: '#f9fafb',
        fontStyle: 'italic',
        fontSize: 9,
        color: '#64748b'
    },
    totalSummaryBox: {
        marginTop: 30,
        padding: 15,
        backgroundColor: '#f0f9ff',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#bae6fd',
        alignItems: 'flex-end'
    },
    totalSummaryLabel: {
        fontSize: 10,
        color: '#0369a1',
        marginBottom: 4
    },
    totalSummaryValue: {
        fontSize: 18,
        fontWeight: 'bold',
        color: '#0369a1'
    },
    freeTextBox: {
        marginTop: 30,
        padding: 24,
        backgroundColor: '#ffffff',
        borderRadius: 8,
        width: '100%',
        borderLeft: '4pt solid #10b981',
        borderWidth: 1,
        borderColor: '#e2e8f0',
        borderLeftColor: '#10b981'
    },
    signatureSection: {
        marginTop: 50,
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 40
    },
    signatureBox: {
        flex: 1,
        borderTopWidth: 1,
        borderTopColor: '#94a3b8',
        paddingTop: 8,
        alignItems: 'center'
    },
    signatureLabel: {
        fontSize: 9,
        color: '#64748b'
    },
    // Table Styles
    table: {
        display: 'table',
        width: 'auto',
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#e2e8f0',
        borderRightWidth: 0,
        borderBottomWidth: 0,
        marginBottom: 20
    },
    tableRow: {
        margin: 'auto',
        flexDirection: 'row'
    },
    tableColHeader: {
        width: '25%',
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#e2e8f0',
        borderLeftWidth: 0,
        borderTopWidth: 0,
        backgroundColor: '#f8fafc',
        padding: 8
    },
    tableCol: {
        width: '25%',
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#e2e8f0',
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 8
    },
    tableCellHeader: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#475569'
    },
    tableCell: {
        fontSize: 9,
        color: '#334155'
    }
});

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const dayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    return `${dayNames[date.getDay()]}, ${date.toLocaleDateString('de-DE')}`;
};

const htmlStyles = StyleSheet.create({
    html: { fontSize: 10, color: '#334155' },
    p: { fontSize: 10, lineHeight: 1.2, margin: 0, padding: 0 },
    h1: { fontSize: 13, fontWeight: 'bold', color: '#0f172a', margin: 0, marginBottom: 2, marginTop: 4 },
    h2: { fontSize: 11, fontWeight: 'bold', color: '#0f172a', margin: 0, marginBottom: 2, marginTop: 4 },
    h3: { fontSize: 10, fontWeight: 'bold', color: '#0f172a', margin: 0, marginBottom: 2, marginTop: 4 },
    ul: { margin: 0, paddingLeft: 10 },
    ol: { margin: 0, paddingLeft: 10 },
    li: { fontSize: 10, lineHeight: 1.2, margin: 0, padding: 0 },
});

const TimesheetPdfDocument = ({ entries, reportTitle, employeeName, filterText, creatorName, includeNotes = true, includeDetails = true, freeTextHtml, freeTextPosition = 'before_signature' }) => {
    const totalHours = entries.reduce((sum, entry) => sum + parseFloat(entry.hours || 0), 0);
    const displayTitle = reportTitle && reportTitle.trim() !== '' ? reportTitle : 'Stundennachweis';

    const renderFreeText = () => {
        if (!freeTextHtml) return null;
        const plainText = freeTextHtml.replace(/<[^>]*>/g, '').trim();
        if (!plainText) return null;

        return (
            <View style={styles.freeTextBox} wrap={false}>
                <Html stylesheet={htmlStyles}>{freeTextHtml}</Html>
            </View>
        );
    };

    return (
        <Document>
            {/* Cover Page */}
            <Page size="A4" style={styles.coverPage}>
                <View style={{ marginBottom: 40, alignItems: 'center' }}>
                    <Text style={styles.coverTitle}>{displayTitle}</Text>
                    <Text style={styles.coverSubtitle}>{employeeName}</Text>
                </View>

                <View style={styles.coverInfoBox}>
                    <Text style={styles.coverLabel}>Mitarbeiter</Text>
                    <Text style={styles.coverValue}>{employeeName || '-'}</Text>

                    <Text style={styles.coverLabel}>Exportzeitraum</Text>
                    <Text style={styles.coverValue}>{filterText || 'Gesamter Zeitraum'}</Text>

                    <Text style={styles.coverLabel}>Anzahl Einträge</Text>
                    <Text style={styles.coverValue}>{entries.length}</Text>

                    <View style={{ marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#e2e8f0' }}>
                        <Text style={styles.coverLabel}>Gesamtstunden</Text>
                        <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#3b82f6' }}>{totalHours.toFixed(2)} h</Text>
                    </View>

                    <View style={{ marginTop: 20 }}>
                        <Text style={styles.coverLabel}>Ausstellungsdatum</Text>
                        <Text style={styles.coverValue}>{new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</Text>

                        {creatorName && (
                            <>
                                <Text style={styles.coverLabel}>Exportiert von</Text>
                                <Text style={styles.coverValue}>{creatorName}</Text>
                            </>
                        )}
                    </View>
                </View>
            </Page>

            {/* Special Position: Page after Cover */}
            {freeTextPosition === 'between_cover_entries' && freeTextHtml && (
                <Page size="A4" style={styles.page}>
                    <View style={styles.header}>
                        <Text style={styles.headerText}>{employeeName}</Text>
                        <Text style={styles.headerText}>{displayTitle}</Text>
                    </View>
                    <View style={{ marginTop: 20 }}>
                        {renderFreeText()}
                    </View>
                </Page>
            )}

            {/* Content Pages */}
            <Page size="A4" style={styles.page} wrap>
                {/* Repeating Header */}
                <View style={styles.header} fixed>
                    <Text style={styles.headerText}>{employeeName}</Text>
                    <Text style={styles.headerText}>{displayTitle}</Text>
                </View>

                {/* List of Entries */}
                {includeDetails ? (
                    entries.map((entry, index) => {
                        let tasks = [];
                        try {
                            tasks = typeof entry.tasks === 'string' ? JSON.parse(entry.tasks) : (entry.tasks || []);
                        } catch (e) {
                            tasks = [{ description: entry.activity, hours: entry.hours }];
                        }
                        if (!tasks || tasks.length === 0) {
                            tasks = [{ description: entry.activity || 'Keine Angabe', hours: entry.hours }];
                        }

                        const statusKey = entry.deletion_request_pending ? 'deletion_pending' : (entry.status || 'entwurf');
                        const status = STATUS_COLORS[statusKey] || STATUS_COLORS.entwurf;

                        return (
                            <View key={entry.id || index} style={styles.entryContainer} wrap={false}>
                                <View style={styles.entryHeaderRow}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                        <Text style={styles.entryDateTitle}>{formatDate(entry.entry_date)}</Text>
                                        <View style={[styles.badge, { backgroundColor: status.bg, borderColor: status.border, color: status.text }]}>
                                            <Text>{status.label}</Text>
                                        </View>
                                    </View>
                                    <Text style={styles.dayTotalText}>{parseFloat(entry.hours).toFixed(2)} h</Text>
                                </View>

                                <View>
                                    {tasks.map((task, tIdx) => (
                                        <View key={tIdx} style={styles.taskRow}>
                                            <Text style={styles.taskDescription}>{task.description}</Text>
                                            <Text style={styles.taskHours}>{parseFloat(task.hours).toFixed(2)} h</Text>
                                        </View>
                                    ))}
                                </View>

                                {includeNotes && entry.notes && (
                                    <View style={styles.notesBox}>
                                        <Text>Notiz: {entry.notes}</Text>
                                    </View>
                                )}
                            </View>
                        );
                    })
                ) : (
                    /* Compact Table View */
                    <View style={styles.table}>
                        {/* Header */}
                        <View style={styles.tableRow}>
                            <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Wochentag</Text></View>
                            <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Datum</Text></View>
                            <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Status</Text></View>
                            <View style={styles.tableColHeader}><Text style={styles.tableCellHeader}>Stunden</Text></View>
                        </View>
                        {/* Body */}
                        {entries.map((entry, index) => {
                            const statusKey = entry.deletion_request_pending ? 'deletion_pending' : (entry.status || 'entwurf');
                            const statusLabel = (STATUS_COLORS[statusKey] || STATUS_COLORS.entwurf).label;
                            const date = new Date(entry.entry_date);
                            const dayName = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][date.getDay()];

                            return (
                                <View key={entry.id || index} style={styles.tableRow}>
                                    <View style={styles.tableCol}><Text style={styles.tableCell}>{dayName}</Text></View>
                                    <View style={styles.tableCol}><Text style={styles.tableCell}>{date.toLocaleDateString('de-DE')}</Text></View>
                                    <View style={styles.tableCol}><Text style={styles.tableCell}>{statusLabel}</Text></View>
                                    <View style={styles.tableCol}><Text style={styles.tableCell}>{parseFloat(entry.hours).toFixed(2)} h</Text></View>
                                </View>
                            );
                        })}
                    </View>
                )}

                {/* Position: Before Total */}
                {freeTextPosition === 'before_total' && renderFreeText()}

                {/* Final Total Summary */}
                <View style={styles.totalSummaryBox} wrap={false}>
                    <Text style={styles.totalSummaryLabel}>Gesamtstunden Zeitraum</Text>
                    <Text style={styles.totalSummaryValue}>{totalHours.toFixed(2)} h</Text>
                </View>

                {/* Position: Before Signature */}
                {freeTextPosition === 'before_signature' && renderFreeText()}

                {/* Signatures */}
                <View style={styles.signatureSection} wrap={false}>
                    <View style={styles.signatureBox}>
                        <Text style={styles.signatureLabel}>Datum / Unterschrift Mitarbeiter</Text>
                    </View>
                    <View style={styles.signatureBox}>
                        <Text style={styles.signatureLabel}>Datum / Unterschrift Vorgesetzter</Text>
                    </View>
                </View>

                {/* Position: After Signature */}
                {freeTextPosition === 'after_signature' && renderFreeText()}

                {/* Repeating Footer */}
                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>
                        {employeeName} • {displayTitle} • {new Date().toLocaleDateString('de-DE')}
                    </Text>
                    <Text style={styles.footerText} render={({ pageNumber, totalPages }) => (
                        `Seite ${pageNumber} von ${totalPages}`
                    )} />
                </View>
            </Page>
        </Document>
    );
};

export default TimesheetPdfDocument;
